import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";
import { getSportsProvider, getActiveProviderId, type ProviderId } from "./providers";
import { DemoProvider } from "./providers/DemoProvider";
import {
  writeSportsCache,
  writeSportsProviderHealth,
} from "@/lib/sportsCache.functions";
import type {
  ProviderMatchDetails, ProviderLineup, ProviderInjury, ProviderSuspension,
  ProviderCard, ProviderHeadToHead, ProviderStandings, ProviderFixture,
  ProviderMatchEvent,
  ProviderFixturePlayers,
} from "./providers/types";

interface CacheCfg { matches: number; stats: number; h2h: number }
let cacheCfg: CacheCfg = { matches: 15, stats: 30, h2h: 1440 };

/**
 * LIVE-scope TTL. Events, live stats, per-fixture players and live match
 * details change every few seconds during a match — a 30-min cache means
 * an empty `[]` gets pinned for the entire first half. Keep them at 1 min
 * so React Query's 30–60 s polling actually revalidates through the cache.
 */
const LIVE_TTL_MIN = 1;
let realDataEnabled = false;
let configLoaded = false;

async function ensureConfig() {
  if (configLoaded) return;
  configLoaded = true;
  try {
    const { data } = await supabase.from("feature_flags")
      .select("key,enabled,payload")
      .in("key", ["real_sports_data_enabled", "sports_cache_ttl_minutes"]);
    (data ?? []).forEach((r) => {
      if (r.key === "real_sports_data_enabled") realDataEnabled = !!r.enabled;
      if (r.key === "sports_cache_ttl_minutes") {
        const p = (r.payload ?? {}) as Partial<CacheCfg>;
        cacheCfg = { ...cacheCfg, ...p };
      }
    });
  } catch { /* keep defaults */ }
}

function activeProviderName(): ProviderId {
  return realDataEnabled ? getActiveProviderId() : "demo";
}

async function recordHealth(provider: string, success: boolean, error?: string) {
  // Writes must bypass RLS; browser roles cannot INSERT/UPDATE these tables.
  // The server fn uses the service-role client and logs any failure.
  try {
    const res = await writeSportsProviderHealth({
      data: { provider, success, error: error ?? null },
    });
    if (!res.ok) {
      console.error("[unifiedSports.recordHealth] server write failed", {
        provider,
        error: res.error,
      });
    }
  } catch (err) {
    console.error("[unifiedSports.recordHealth] server fn threw", {
      provider,
      err: String(err),
    });
  }
}

async function readCache<T>(table: "sports_matches_cache" | "sports_stats_cache" | "sports_h2h_cache",
  filter: Record<string, string>): Promise<T | null> {
  try {
    let q = supabase.from(table).select("payload_json,expires_at");
    Object.entries(filter).forEach(([k, v]) => { q = q.eq(k, v); });
    const { data } = await q.maybeSingle();
    if (!data) return null;
    if (new Date(data.expires_at).getTime() < Date.now()) return null;
    void trackEvent("sports_cache_hit", { table, ...filter });
    return data.payload_json as T;
  } catch { return null; }
}

async function writeCache(table: "sports_matches_cache" | "sports_stats_cache" | "sports_h2h_cache",
  row: Record<string, unknown>, ttlMin: number) {
  // Routed through a server fn using the service-role client so RLS on the
  // cache tables (admin-only INSERT/UPDATE) doesn't drop the write silently.
  try {
    const res = await writeSportsCache({ data: { table, row, ttlMin } });
    if (!res.ok) {
      console.error("[unifiedSports.writeCache] server write failed", {
        table,
        error: res.error,
      });
    }
  } catch (err) {
    console.error("[unifiedSports.writeCache] server fn threw", {
      table,
      err: String(err),
    });
  }
}

// Note: conflict-columns per table are resolved server-side inside the
// `writeSportsCache` server function; the browser never needs to know them.

async function withFailover<T>(opName: string, fn: (provider: ReturnType<typeof getSportsProvider>) => Promise<T>,
  cacheRead: () => Promise<T | null>, cacheWrite: (data: T) => Promise<void>): Promise<T> {
  await ensureConfig();
  const cached = await cacheRead();
  if (cached) return cached;
  void trackEvent("sports_cache_miss", { op: opName });
  const providerId = activeProviderName();
  const provider = getSportsProvider(providerId);
  try {
    const data = await fn(provider);
    void trackEvent("sports_provider_synced", { op: opName, provider: providerId });
    void recordHealth(providerId, true);
    void cacheWrite(data);
    return data;
  } catch (err) {
    void trackEvent("sports_provider_failed", { op: opName, provider: providerId, error: String(err) });
    void recordHealth(providerId, false, String(err));
    if (providerId !== "demo") {
      void trackEvent("fallback_to_mock_provider", { op: opName });
      const data = await fn(DemoProvider);
      void cacheWrite(data);
      return data;
    }
    throw err;
  }
}

export const unifiedSports = {
  async getMatchDetails(matchId: string): Promise<ProviderMatchDetails> {
    const source = activeProviderName();
    return withFailover(
      "getMatchDetails",
      (p) => p.getMatchDetails(matchId),
      async () => {
        const cached = await readCache<ProviderMatchDetails>(
          "sports_matches_cache",
          { match_id: matchId, source },
        );
        if (!cached) return null;
        // Guard: never serve a demo/non-official payload that was mistakenly
        // written under source='api_football'. It desynchronizes Header,
        // Scoreboard and MatchCtx from the official Timeline.
        if (
          source === "api_football" &&
          (cached.provider === "demo" || cached.official === false)
        ) {
          void trackEvent("sports_cache_miss", {
            op: "getMatchDetails:poisoned_skipped",
            matchId,
            source,
            provider: cached.provider,
            official: cached.official,
          });
          return null;
        }
        return cached;
      },
      (data) => {
        // Never persist a demo payload under the api_football source.
        if (
          source === "api_football" &&
          (data?.provider === "demo" || data?.official === false)
        ) {
          void trackEvent("sports_provider_failed", {
            op: "getMatchDetails:poisoned_write_blocked",
            matchId,
            provider: data?.provider,
            official: data?.official,
          });
          return Promise.resolve();
        }
        // Live matches must refresh score/minute frequently; scheduled/finished
        // matches can keep the normal TTL.
        const status = (data as { status?: string } | null)?.status;
        const isLive = status === "LIVE";
        return writeCache(
          "sports_matches_cache",
          { match_id: matchId, source, payload_json: data as never },
          isLive ? LIVE_TTL_MIN : cacheCfg.matches,
        );
      },
    );
  },
  async getLineups(fixtureId: string): Promise<ProviderLineup> {
    const source = activeProviderName();
    return withFailover(
      "getLineups",
      (p) => p.getLineups(fixtureId),
      () => readCache("sports_stats_cache", { scope: "lineups", scope_id: fixtureId, source }),
      (data) => {
        // Official lineups (both starting XI complete) don't change once
        // published — cache them for 6h so a single upstream hit serves the
        // whole match window and survives API-Football rate-limit windows.
        // Empty/partial payloads keep the default short TTL so we keep
        // revalidating until official data arrives.
        const isOfficialComplete =
          !!data?.official &&
          (data?.home?.startXI?.length ?? 0) === 11 &&
          (data?.away?.startXI?.length ?? 0) === 11;
        const ttl = isOfficialComplete ? 360 : cacheCfg.stats;
        return writeCache(
          "sports_stats_cache",
          { scope: "lineups", scope_id: fixtureId, source, payload_json: data as never },
          ttl,
        );
      },
    );
  },
  async getInjuries(teamId: string): Promise<ProviderInjury[]> {
    const source = activeProviderName();
    return withFailover(
      "getInjuries",
      (p) => p.getInjuries(teamId),
      () => readCache("sports_stats_cache", { scope: "injuries", scope_id: teamId, source }),
      (data) => writeCache("sports_stats_cache", { scope: "injuries", scope_id: teamId, source, payload_json: data as never }, cacheCfg.stats),
    );
  },
  async getSuspensions(teamId: string): Promise<ProviderSuspension[]> {
    const source = activeProviderName();
    return withFailover(
      "getSuspensions",
      (p) => p.getSuspensions(teamId),
      () => readCache("sports_stats_cache", { scope: "suspensions", scope_id: teamId, source }),
      (data) => writeCache("sports_stats_cache", { scope: "suspensions", scope_id: teamId, source, payload_json: data as never }, cacheCfg.stats),
    );
  },
  async getCards(fixtureId: string): Promise<ProviderCard[]> {
    const source = activeProviderName();
    return withFailover(
      "getCards",
      (p) => p.getCards(fixtureId),
      () => readCache("sports_stats_cache", { scope: "cards", scope_id: fixtureId, source }),
      (data) => writeCache("sports_stats_cache", { scope: "cards", scope_id: fixtureId, source, payload_json: data as never }, cacheCfg.stats),
    );
  },
  async getStats(fixtureId: string): Promise<Record<string, number | string>> {
    const source = activeProviderName();
    return withFailover(
      "getStats",
      (p) => p.getStats(fixtureId),
      () => readCache("sports_stats_cache", { scope: "stats", scope_id: fixtureId, source }),
      (data) => writeCache("sports_stats_cache", { scope: "stats", scope_id: fixtureId, source, payload_json: data as never }, LIVE_TTL_MIN),
    );
  },
  async getH2H(homeTeamId: string, awayTeamId: string, competitionId = "fifa-wc-2026"): Promise<ProviderHeadToHead> {
    const source = activeProviderName();
    return withFailover(
      "getH2H",
      (p) => p.getHeadToHead(homeTeamId, awayTeamId, competitionId),
      () => readCache("sports_h2h_cache", { home_team_id: homeTeamId, away_team_id: awayTeamId, competition_id: competitionId, source }),
      (data) => writeCache("sports_h2h_cache", {
        home_team_id: homeTeamId, away_team_id: awayTeamId, competition_id: competitionId, source, payload_json: data as never,
      }, cacheCfg.h2h),
    );
  },
  async getStandings(competitionId: string): Promise<ProviderStandings> {
    const source = activeProviderName();
    return withFailover(
      "getStandings",
      (p) => p.getStandings(competitionId),
      () => readCache("sports_stats_cache", { scope: "standings", scope_id: competitionId, source }),
      (data) => writeCache("sports_stats_cache", { scope: "standings", scope_id: competitionId, source, payload_json: data as never }, cacheCfg.stats),
    );
  },
  async getEvents(fixtureId: string): Promise<ProviderMatchEvent[]> {
    const source = activeProviderName();
    return withFailover(
      "getEvents",
      (p) => p.getEvents(fixtureId),
      () => readCache("sports_stats_cache", { scope: "events", scope_id: fixtureId, source }),
      (data) => writeCache("sports_stats_cache", { scope: "events", scope_id: fixtureId, source, payload_json: data as never }, LIVE_TTL_MIN),
    );
  },
  /**
   * Real Data Phase 4 — Official per-fixture player statistics
   * (`/fixtures/players`). No failover to DemoProvider: on real-provider
   * failure we surface the empty payload so the UI shows the
   * lifecycle-appropriate placeholder from `dataPolicy.ts` instead of
   * fabricated ratings.
   */
  async getFixturePlayers(fixtureId: string): Promise<ProviderFixturePlayers> {
    await ensureConfig();
    const source = activeProviderName();
    const cached = await readCache<ProviderFixturePlayers>(
      "sports_stats_cache",
      { scope: "fixture_players", scope_id: fixtureId, source },
    );
    if (cached) return cached;
    void trackEvent("sports_cache_miss", { op: "getFixturePlayers" });
    const provider = getSportsProvider(source);
    try {
      const data = await provider.getFixturePlayers(fixtureId);
      void trackEvent("sports_provider_synced", { op: "getFixturePlayers", provider: source });
      void recordHealth(source, true);
      void writeCache(
        "sports_stats_cache",
        { scope: "fixture_players", scope_id: fixtureId, source, payload_json: data as never },
        LIVE_TTL_MIN,
      );
      return data;
    } catch (err) {
      void trackEvent("sports_provider_failed", { op: "getFixturePlayers", provider: source, error: String(err) });
      void recordHealth(source, false, String(err));
      // Never fabricate — return empty official=false payload.
      return { fixtureId, official: false, provider: source, home: null, away: null };
    }
  },
  async getFixtures(): Promise<ProviderFixture[]> {
    const source = activeProviderName();
    return withFailover(
      "getFixtures",
      (p) => p.getFixtures(),
      () => readCache("sports_stats_cache", { scope: "fixtures", scope_id: "all", source }),
      (data) => writeCache("sports_stats_cache", { scope: "fixtures", scope_id: "all", source, payload_json: data as never }, cacheCfg.matches),
    );
  },
  async refreshAll(): Promise<void> {
    configLoaded = false;
    await ensureConfig();
  },
  getActiveProvider(): ProviderId { return activeProviderName(); },
  getCacheConfig(): CacheCfg { return cacheCfg; },
};
